import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SPREAD_RATIO_LIMITS } from '@/slideshow/settings';
import { sheetRatioFromDrag } from '@components/shared/spreadLayoutUtils';

const SHEET_EDGES = [
    { edge: 'left', className: 'inset-y-0 left-0 w-2 cursor-ew-resize' },
    { edge: 'right', className: 'inset-y-0 right-0 w-2 cursor-ew-resize' },
    { edge: 'top', className: 'inset-x-0 top-0 h-2 cursor-ns-resize' },
    { edge: 'bottom', className: 'inset-x-0 bottom-0 h-2 cursor-ns-resize' },
];

const clampRatio = (ratio) => Math.min(SPREAD_RATIO_LIMITS.max, Math.max(SPREAD_RATIO_LIMITS.min, ratio));

/**
 * Drag handles on a sheet's four edges. A centred sheet grows on both sides, so
 * the edge stays under the pointer. `onResize(ratio, done)` reports width / height
 * while dragging, then once more on release (null when cancelled).
 */
export default function SheetEdgeHandles({ align = 'mc', onResize }) {
    const dragRef = useRef(null);
    const [live, setLive] = useState(null);

    const start = (spec) => (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.parentElement.getBoundingClientRect();
        dragRef.current = { spec, x: event.clientX, y: event.clientY, width: rect.width, height: rect.height, ratio: null };
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone; moves still arrive while it is over the edge.
        }
    };
    const move = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const ratio = clampRatio(sheetRatioFromDrag({
            width: drag.width,
            height: drag.height,
            edge: drag.spec.edge,
            dx: event.clientX - drag.x,
            dy: event.clientY - drag.y,
            align,
        }));
        if (ratio === drag.ratio) return;
        drag.ratio = ratio;
        setLive(ratio);
        onResize(ratio, false);
    };
    const finish = (commit) => (event) => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!drag) return;
        setLive(null);
        if (drag.ratio) onResize(commit ? drag.ratio : null, true);
    };

    return (
        <>
            {SHEET_EDGES.map((spec) => (
                <div
                    key={spec.edge}
                    data-sheet-edge={spec.edge}
                    className={`pointer-events-auto absolute z-20 touch-none select-none transition-colors ${live ? 'bg-purple-500/60' : 'hover:bg-purple-500/60'} ${spec.className}`}
                    title="Drag to change the spread's shape"
                    onPointerDown={start(spec)}
                    onPointerMove={move}
                    onPointerUp={finish(true)}
                    onPointerCancel={finish(false)}
                    onClick={(event) => event.stopPropagation()}
                />
            ))}
            {live && (
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded bg-purple-600 px-2 py-1 text-xs font-medium tabular-nums text-white">
                    {live.toFixed(2)} : 1
                </div>
            )}
        </>
    );
}

SheetEdgeHandles.propTypes = {
    align: PropTypes.string,
    onResize: PropTypes.func.isRequired,
};
